import styled from 'styled-components';
import colours from 'theme/atoms/colours';
import breakpoints from 'theme/atoms/breakpoints';
import * as Text from 'theme/components/Text.style';

export const Container = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 0 0 60px 0;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 15px 0 60px 0;
  }
`;

export const TitleGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const Subtitle = styled(Text.P1)`
  color: ${colours.neutral[500]};
`;

export const FieldGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const fieldBase = `
  background-color: white;
  border: 2px solid ${colours.neutral[200]};
  box-sizing: border-box;
  color: ${colours.neutral[900]};
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  padding: 15px 15px 15px 20px;
  width: 100%;

  &::placeholder {
    color: ${colours.neutral[500]};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: ${colours.neutral[400]};
  }
`;

export const Input = styled('input')`
  ${fieldBase}
  border-radius: 30px;
`;

export const Textarea = styled('textarea')`
  ${fieldBase}
  border-radius: 25px;
  min-height: 150px;
  resize: vertical;
`;

interface TriggerProps {
  $hasValue?: boolean;
}

export const FieldTrigger = styled('button')<TriggerProps>`
  ${fieldBase}
  align-items: center;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  text-align: left;

  color: ${({ $hasValue }) =>
    $hasValue ? colours.neutral[900] : colours.neutral[500]};

  svg {
    color: ${colours.neutral[900]};
    flex-shrink: 0;
    height: 20px;
    width: 20px;
  }
`;

export const HiddenDateTextFieldSx = {
  width: 0,
  height: 0,
  overflow: 'hidden',
  position: 'absolute' as const,
  '& .MuiOutlinedInput-root': {
    display: 'none',
  },
};

export const SwitchFieldWrapper = styled('div')`
  display: flex;
  align-items: center;
  min-height: 48px;
`;

export const NumberFieldWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

export const NumberInput = styled('input')`
  ${fieldBase}
  border-radius: 30px;
  padding-right: 60px;
`;

export const StepperGroup = styled('div')`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StepperButton = styled('button')`
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: ${colours.neutral[700]};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${colours.neutral[100]};
    color: ${colours.neutral[900]};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ListFieldWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ListRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ListRowItem = styled('div')`
  flex: 1;
  min-width: 0;
`;

export const ChoiceFieldRoot = styled('div')`
  position: relative;
  width: 100%;
`;

export const ChoiceFieldTrigger = styled('div')<{ $open: boolean }>`
  ${fieldBase}
  align-items: center;
  border-radius: 30px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: ${colours.neutral[400]};
  }

  ${({ $open }) => $open && `border-color: ${colours.neutral[400]};`}
`;

export const ChoiceFieldInput = styled('input')`
  background: transparent;
  border: none;
  outline: none;
  flex: 1;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: ${colours.neutral[900]};
  padding: 0;

  &::placeholder {
    color: ${colours.neutral[500]};
    opacity: 1;
  }
`;

export const ChoiceFieldChevron = styled('button')<{ $open: boolean }>`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colours.neutral[900]};
  transition: transform 0.2s ease;
  flex-shrink: 0;

  ${({ $open }) => $open && `transform: rotate(180deg);`}

  svg {
    stroke-width: 2;
  }
`;
